import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Check, ListTodo, MessageSquare, FlagTriangleRight, Rocket,
  Key, CircleCheck, CircleX, Sunrise, TriangleAlert,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  getNotifications,
  getUnreadCount,
  markRead,
  markAllRead,
  type Notification,
} from '../packages/api-client/notifications-api';

const TYPE_ICONS: Record<string, LucideIcon> = {
  task_assigned:   ListTodo,
  comment_added:   MessageSquare,
  milestone_due:   FlagTriangleRight,
  project_added:   Rocket,
  access_requested:        Key,
  access_request_approved: CircleCheck,
  access_request_denied:   CircleX,
  assistant_briefing:      Sunrise,
  assistant_urgent:        TriangleAlert,
};

function relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)   return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Poll unread count every 30s ───────────────────────────────────────────
  const refreshCount = useCallback(async () => {
    try {
      const count = await getUnreadCount();
      setUnread(count);
    } catch { /* ignore network errors silently */ }
  }, []);

  useEffect(() => {
    refreshCount();
    pollRef.current = setInterval(refreshCount, 30_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [refreshCount]);

  // ── Close drawer on outside click ────────────────────────────────────────
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (open && drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Move focus into the panel when it opens ───────────────────────────────
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
      bellRef.current?.focus();
    }
  }

  // ── Load notifications when drawer opens ─────────────────────────────────
  async function handleOpen() {
    setOpen(v => !v);
    if (!open) {
      setLoading(true);
      try {
        const resp = await getNotifications(false, 30);
        setItems(resp.data);
        setUnread(resp.unread_count);
      } finally {
        setLoading(false);
      }
    }
  }

  // ── Mark one read ────────────────────────────────────────────────────────
  async function handleMarkRead(n: Notification) {
    if (n.route) {
      navigate(n.route);
      setOpen(false);
    }
    if (!n.read_at) {
      try {
        await markRead(n.id);
        setItems(prev => prev.map(item => item.id === n.id ? { ...item, read_at: new Date().toISOString() } : item));
        setUnread(prev => Math.max(0, prev - 1));
      } catch { /* ignore */ }
    }
  }

  // ── Mark all read ────────────────────────────────────────────────────────
  async function handleMarkAllRead() {
    setMarkingAll(true);
    try {
      await markAllRead();
      setItems(prev => prev.map(item => ({ ...item, read_at: item.read_at ?? new Date().toISOString() })));
      setUnread(0);
    } finally { setMarkingAll(false); }
  }

  return (
    <div style={{ position: 'relative' }} ref={drawerRef} onKeyDown={handleKeyDown}>
      {/* Bell button */}
      <button
        ref={bellRef}
        className="notification-bell"
        onClick={handleOpen}
        title="Notifications"
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell size={20} aria-hidden="true" />
        {unread > 0 && (
          <span className="notification-badge">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <div className="notification-drawer" role="dialog" aria-label="Notifications" tabIndex={-1} ref={panelRef}>
          <div className="notification-drawer-header">
            <span className="notification-drawer-title">Notifications</span>
            {unread > 0 && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleMarkAllRead}
                disabled={markingAll}
                style={{ fontSize: 'var(--font-size-xs)' }}
              >
                {markingAll ? '…' : <><Check size={14} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: 4 }} />Mark all read</>}
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <div className="spinner spinner-md" />
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="notification-empty">
                <div style={{ marginBottom: '0.5rem' }}><Bell size={32} aria-hidden="true" /></div>
                <div>You're all caught up!</div>
              </div>
            )}

            {!loading && items.map(n => (
              <button
                key={n.id}
                className={`notification-item${n.read_at ? '' : ' unread'}`}
                onClick={() => handleMarkRead(n)}
              >
                <span className="notification-item-icon">
                  {(() => { const Icon = TYPE_ICONS[n.type] ?? Bell; return <Icon size={18} aria-hidden="true" />; })()}
                </span>
                <span className="notification-item-body">
                  <span className="notification-item-title">{n.title}</span>
                  {n.body && <span className="notification-item-desc">{n.body}</span>}
                  <span className="notification-item-time">{relativeTime(n.created_at)}</span>
                </span>
                {!n.read_at && <span className="notification-unread-dot" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
